import { useTranslation } from 'react-i18next';
import { useUiModeStore } from '@/features/ui-mode/store/ui-mode-store';
import type {
  ChargePointData,
  ChargePointGroupData,
} from '../../types/charge-point.types';

interface AddressSectionProps {
  chargePoint?: ChargePointData;
  chargePointGroup?: ChargePointGroupData;
}

/**
 * Display charge point address and location info
 */
export function AddressSection({
  chargePoint,
  chargePointGroup,
}: AddressSectionProps) {
  const { t } = useTranslation();
  const uiMode = useUiModeStore((s) => s.uiMode);

  // Use charge point data if available, otherwise use group data
  const data = chargePoint || chargePointGroup;

  if (!data) return null;

  const address1 = 'address1' in data ? data.address1 : undefined;
  const city = data.city;
  const zip = 'zip' in data ? data.zip : undefined;
  const addressState = 'addressState' in data ? data.addressState : undefined;
  const operatorName = data.operatorName;

  // Build full address string
  // Note: address2 and address3 often duplicate zip/city, so we skip them
  const zipCity = [zip, city].filter(Boolean).join(' ');
  const addressParts = [address1, zipCity, addressState].filter(Boolean);

  return (
    <div className="space-y-2">
      {/* Address */}
      <div className="flex items-start gap-2">
        <svg
          className="w-5 h-5 text-muted flex-shrink-0 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <div className="flex-1">
          {addressParts.map((part, index) => (
            <p key={index} className="text-sm text-ink/80">
              {part}
            </p>
          ))}
        </div>
      </div>

      {/* Operator Name */}
      {operatorName && (
        <div className="flex items-start gap-2 pl-7">
          {uiMode === 'operator' ? (
            <p className="text-sm text-muted">
              {t('chargePoint.operatorMode.operatorLabel', {
                name: operatorName,
              })}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted">{t('chargePoint.operator')}:</p>
              <p className="text-sm font-semibold text-ink">{operatorName}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
